/**
 * Accommodation Date Change Detector - Phase 2B Task P2B.1.1
 * 
 * This service implements date change detection for accommodation modification scenarios.
 * It subscribes to ACCOMMODATION_UPDATED events and analyzes date changes to determine
 * what type of day entry updates are needed.
 * 
 * The detector classifies changes as:
 * - DATES_ADDED: Previously undated accommodation gets dates (Scenario 2.1)
 * - EXPANSION: Date range expanded, need to add more day entries (Scenario 2.2)
 * - CONTRACTION: Date range contracted, need to remove day entries (Scenario 2.3)
 * - SHIFT_SAME_DURATION: Dates shifted but same duration (Scenario 2.4)
 * - SHIFT_DIFFERENT_DURATION: Dates shifted with different duration (Scenario 2.7)
 * - DATES_REMOVED: Dates removed from accommodation (Scenario 2.6)
 * 
 * This service works with the existing suggestion system to trigger appropriate
 * modification suggestions based on the detected change type.
 * 
 * INTEGRATION EXAMPLE:
 * ```javascript
 * // In suggestionService.js initialization
 * import { accommodationDateChangeDetector } from './accommodationDateChangeDetector';
 * 
 * // Initialize with a handler that creates modification suggestions
 * accommodationDateChangeDetector.init(async (analysis) => {
 *   if (analysis.type === 'EXPANSION') {
 *     // Generate day entry addition suggestions
 *     await suggestionService.createSuggestion({
 *       type: 'DAY_UPDATES_FOR_CHANGED_ACCOMMODATION',
 *       action: 'ADD_DAY_ENTRIES',
 *       data: analysis
 *     });
 *   } else if (analysis.type === 'CONTRACTION') {
 *     // Generate day entry removal suggestions with user confirmation
 *     await suggestionService.createSuggestion({
 *       type: 'DAY_UPDATES_FOR_CHANGED_ACCOMMODATION', 
 *       action: 'REMOVE_DAY_ENTRIES',
 *       data: analysis,
 *       requiresConfirmation: true
 *     });
 *   }
 *   // Handle other change types...
 * });
 * ```
 * 
 * NEXT STEPS FOR PHASE 2B:
 * - Task P2B.1.2: Create SuggestDayUpdatesForChangedAccommodation rule
 * - Task P2B.1.3: Implement day entry cleanup logic with user confirmation
 * - Integration with GenericSuggestionPopup for modification scenarios
 */

import { eventBridgeService, EVENT_TYPES } from '@trip-planner-monorepo/shared';
import { accommodationDayUtils } from '@trip-planner-monorepo/days';

// Extract the utilities we need from the imported module
const {
  analyzeDateRangeChanges,
  isContractionScenario,
  isExpansionScenario,
  analyzeShiftScenario,
  generateDateChangeSuggestions,
  validateDateChanges
} = accommodationDayUtils;

/**
 * @typedef {Object} DateChangeAnalysis
 * @property {string} type - The type of change (DATES_ADDED, EXPANSION, CONTRACTION, etc.)
 * @property {boolean} hasChanges - Whether any changes were detected
 * @property {Object} oldRange - The previous date range
 * @property {Object} newRange - The new date range
 * @property {string[]} addedDays - Days that need new entries
 * @property {string[]} removedDays - Days that need entries removed
 * @property {string[]} overlappingDays - Days that exist in both ranges
 * @property {Object} validation - Validation results for the date changes
 * @property {Array} suggestions - Generated suggestions for the changes
 */

/**
 * Accommodation Date Change Detector Service
 * 
 * This service provides the core date change detection functionality for Phase 2B
 * modification scenarios. It analyzes accommodation updates to determine what
 * day entry changes are needed and generates appropriate suggestions.
 */
export const accommodationDateChangeDetector = {
  
  /**
   * Subscription to accommodation update events
   * @private
   */
  _subscription: null,
  
  /**
   * Callback function for handling detected date changes
   * @private
   */
  _changeHandler: null,
  
  /**
   * Initialize the date change detector
   *
   * @param {Function} [changeHandler] - Optional callback function that receives date change analysis
   *                                     If not provided, the detector will log analysis results
   *                                     This function will be called when date changes are detected
   *                                     with the analysis results and can trigger suggestion generation
   */
  init(changeHandler) {
    console.log('AccommodationDateChangeDetector: Initializing date change detection service');

    // Use provided changeHandler if it's a function, otherwise default to logging
    this._changeHandler = (typeof changeHandler === 'function')
      ? changeHandler
      : this._defaultChangeHandler.bind(this);
    
    // Subscribe to accommodation update events
    this._subscription = eventBridgeService.subscribe(
      EVENT_TYPES.ACCOMMODATION_UPDATED,
      this._handleAccommodationUpdate.bind(this)
    );
    
    console.log('AccommodationDateChangeDetector: Successfully subscribed to ACCOMMODATION_UPDATED events');
    console.log('AccommodationDateChangeDetector: Phase 2B date change detection is now active');
  },
  
  /**
   * Default change handler that logs analysis results
   * Used when no custom change handler is provided
   * 
   * @private
   * @param {Object} analysis - The date change analysis results
   */
  async _defaultChangeHandler(analysis) {
    console.log('AccommodationDateChangeDetector: Date change detected for Phase 2B processing:');
    console.log(`  Accommodation: ${analysis.metadata.accommodationName} (${analysis.metadata.accommodationId})`);
    console.log(`  Scenario: ${analysis.scenario} (${analysis.type})`);
    console.log(`  Added Days: ${analysis.addedDays?.length || 0}`);
    console.log(`  Removed Days: ${analysis.removedDays?.length || 0}`);
    console.log(`  Suggestions Generated: ${analysis.suggestions?.length || 0}`);
    
    // Log each suggestion for debugging
    if (analysis.suggestions && analysis.suggestions.length > 0) {
      analysis.suggestions.forEach((suggestion, index) => {
        console.log(`    Suggestion ${index + 1}: ${suggestion.type} - ${suggestion.description}`);
      });
    }
    
    console.log('AccommodationDateChangeDetector: Analysis complete - ready for Phase 2B rule integration');
  },

  /**
   * Shutdown the date change detector and cleanup subscriptions
   */
  shutdown() {
    console.log('AccommodationDateChangeDetector: Shutting down date change detection service');
    
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }
    
    this._changeHandler = null;
    console.log('AccommodationDateChangeDetector: Successfully unsubscribed from events');
  },
  
  /**
   * Handle accommodation update events and detect date changes
   * 
   * @private
   * @param {Object} eventData - The accommodation update event data
   * @param {Object} eventData.oldAccommodation - The previous accommodation object
   * @param {Object} eventData.newAccommodation - The new accommodation object  
   * @param {string} eventData.userId - User ID
   * @param {string} eventData.tripId - Trip ID
   * @param {string} eventData.id - Accommodation ID
   */
  async _handleAccommodationUpdate(eventData) {
    try {
      console.log('AccommodationDateChangeDetector: Processing accommodation update event:', eventData.id);
      console.log('AccommodationDateChangeDetector: Event data keys:', Object.keys(eventData));
      
      // Extract old and new details from the accommodation objects
      const oldDetails = eventData.oldAccommodation?.details;
      const newDetails = eventData.newAccommodation?.details || eventData.details;
      
      // Validate event data
      if (!oldDetails || !newDetails) {
        console.warn('AccommodationDateChangeDetector: Missing accommodation details in event data');
        console.log('AccommodationDateChangeDetector: Available data:', {
          hasOldAccommodation: !!eventData.oldAccommodation,
          hasNewAccommodation: !!eventData.newAccommodation,
          hasDetails: !!eventData.details,
          oldDetails: !!oldDetails,
          newDetails: !!newDetails
        });
        return;
      }
      
      if (!eventData.userId || !eventData.tripId || !eventData.id) {
        console.warn('AccommodationDateChangeDetector: Missing required identifiers in event data');
        return;
      }
      
      // Analyze date range changes using existing utility
      const changeAnalysis = analyzeDateRangeChanges(oldDetails, newDetails);
      
      console.log('AccommodationDateChangeDetector: Date change analysis result:', {
        type: changeAnalysis.type,
        hasChanges: changeAnalysis.hasChanges,
        addedDays: changeAnalysis.addedDays?.length || 0,
        removedDays: changeAnalysis.removedDays?.length || 0,
        oldCheckIn: oldDetails.checkInDate,
        oldCheckOut: oldDetails.checkOutDate,
        newCheckIn: newDetails.checkInDate,
        newCheckOut: newDetails.checkOutDate
      });
      
      // Skip if no date changes detected
      if (!changeAnalysis.hasChanges) {
        console.log('AccommodationDateChangeDetector: No date changes detected, skipping processing');
        return;
      }
      
      // Validate the date changes
      const validation = validateDateChanges(oldDetails, newDetails);
      
      if (!validation.isValid) {
        console.error('AccommodationDateChangeDetector: Invalid date changes detected:', validation.errors);
        // Could publish an error event here for user notification
        return;
      }
      
      // Log warnings if any
      if (validation.warnings.length > 0) {
        console.warn('AccommodationDateChangeDetector: Date change warnings:', validation.warnings);
      }
      
      // Generate suggestions for the detected changes
      const accommodationForSuggestions = eventData.newAccommodation || {
        id: eventData.id,
        details: newDetails,
        type: eventData.type,
        summary: eventData.summary
      };
      const suggestions = generateDateChangeSuggestions(changeAnalysis, accommodationForSuggestions);
      
      console.log('AccommodationDateChangeDetector: Generated suggestions:', {
        count: suggestions.length,
        types: suggestions.map(s => s.type)
      });
      
      // Create comprehensive analysis object
      const analysis = {
        ...changeAnalysis,
        validation,
        suggestions,
        metadata: {
          accommodationId: eventData.id,
          accommodationName: newDetails?.name || eventData.summary || 'Unknown Accommodation',
          userId: eventData.userId,
          tripId: eventData.tripId,
          timestamp: new Date().toISOString(),
          eventId: `date_change_${Date.now()}`
        }
      };
      
      // Determine scenario classification for documentation tracking
      analysis.scenario = this._classifyScenario(changeAnalysis);
      
      console.log('AccommodationDateChangeDetector: Classified as scenario:', analysis.scenario);
      
      // Call the change handler with the analysis
      if (this._changeHandler) {
        await this._changeHandler(analysis);
      }
      
      console.log('AccommodationDateChangeDetector: Successfully processed date change detection');
      
    } catch (error) {
      console.error('AccommodationDateChangeDetector: Error processing accommodation update:', error);
      console.error('AccommodationDateChangeDetector: Error stack:', error.stack);
    }
  },
  
  /**
   * Classify the change type into scenario numbers for documentation tracking
   * 
   * @private
   * @param {Object} changeAnalysis - The change analysis result
   * @returns {string} The scenario classification (e.g., "2.1", "2.2", etc.)
   */
  _classifyScenario(changeAnalysis) {
    switch (changeAnalysis.type) {
    case 'DATES_ADDED':
      return '2.1'; // Adding Dates to Previously Undated Accommodation
    case 'EXPANSION':
      return '2.2'; // Changing Accommodation Dates (Expanding Range)
    case 'CONTRACTION':
      return '2.3'; // Changing Accommodation Dates (Contracting Range)
    case 'SHIFT_SAME_DURATION':
      return '2.4'; // Shifting Accommodation Dates (Same Duration)
    case 'DATES_REMOVED':
      return '2.6'; // Removing Dates from Accommodation
    case 'SHIFT_DIFFERENT_DURATION':
    case 'COMPLETE_SHIFT':
      return '2.7'; // Shifting Accommodation Dates with Different Duration
    default:
      return 'UNKNOWN';
    }
  },
  
  /**
   * Manually analyze date changes for testing or debugging purposes
   * 
   * @param {Object} oldDetails - Previous accommodation details
   * @param {Object} newDetails - New accommodation details
   * @param {Object} accommodation - Full accommodation object
   * @returns {Object} Analysis results
   */
  analyzeChanges(oldDetails, newDetails, accommodation) {
    console.log('AccommodationDateChangeDetector: Manual analysis requested');
    
    try {
      const changeAnalysis = analyzeDateRangeChanges(oldDetails, newDetails);
      const validation = validateDateChanges(oldDetails, newDetails);
      const suggestions = generateDateChangeSuggestions(changeAnalysis, accommodation);
      
      return {
        ...changeAnalysis,
        validation,
        suggestions,
        scenario: this._classifyScenario(changeAnalysis)
      };
    } catch (error) {
      console.error('AccommodationDateChangeDetector: Error in manual analysis:', error);
      throw error;
    }
  },
  
  /**
   * Get detailed information about a specific change type
   * 
   * @param {string} changeType - The change type to get info about
   * @returns {Object} Information about the change type
   */
  getChangeTypeInfo(changeType) {
    const changeTypeInfo = {
      'DATES_ADDED': {
        scenario: '2.1',
        description: 'Adding dates to previously undated accommodation',
        priority: 'HIGH',
        requiresUserConfirmation: false,
        suggestedActions: ['ADD_DAY_ENTRIES']
      },
      'EXPANSION': {
        scenario: '2.2',
        description: 'Expanding accommodation date range',
        priority: 'HIGH',
        requiresUserConfirmation: false,
        suggestedActions: ['ADD_DAY_ENTRIES']
      },
      'CONTRACTION': {
        scenario: '2.3',
        description: 'Contracting accommodation date range',
        priority: 'HIGH',
        requiresUserConfirmation: true,
        suggestedActions: ['REMOVE_DAY_ENTRIES']
      },
      'SHIFT_SAME_DURATION': {
        scenario: '2.4',
        description: 'Shifting dates with same duration',
        priority: 'HIGH',
        requiresUserConfirmation: true,
        suggestedActions: ['REMOVE_DAY_ENTRIES', 'ADD_DAY_ENTRIES', 'UPDATE_DAY_ENTRIES']
      },
      'DATES_REMOVED': {
        scenario: '2.6',
        description: 'Removing dates from accommodation',
        priority: 'MEDIUM',
        requiresUserConfirmation: true,
        suggestedActions: ['REMOVE_DAY_ENTRIES']
      },
      'SHIFT_DIFFERENT_DURATION': {
        scenario: '2.7',
        description: 'Shifting dates with different duration',
        priority: 'HIGH',
        requiresUserConfirmation: true,
        suggestedActions: ['REMOVE_DAY_ENTRIES', 'ADD_DAY_ENTRIES']
      },
      'COMPLETE_SHIFT': {
        scenario: '2.7',
        description: 'Complete shift to different time period',
        priority: 'HIGH',
        requiresUserConfirmation: true,
        suggestedActions: ['REMOVE_DAY_ENTRIES', 'ADD_DAY_ENTRIES']
      }
    };
    
    return changeTypeInfo[changeType] || {
      scenario: 'UNKNOWN',
      description: 'Unknown change type',
      priority: 'LOW',
      requiresUserConfirmation: true,
      suggestedActions: []
    };
  }
};

export default accommodationDateChangeDetector; 